// TEACH-1 — **HER TEACHING ROOM.** *"No CC builds needed for corrections ever again."*
//
// The LAW is `src/teachingRoom.js` and this file decides nothing: which classes exist, what may be
// saved, what a refusal says and what Lala is handed are all answered there, so a `.mjs` gate RUNS
// them (R146) and this surface cannot hold a second opinion about any of it (R28/R29).
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  TEACHING_CLASSES,
  TEACHING_BODY_MAX,
  TEACHING_SUBJECT_MAX,
  teachingClass,
  teachingLedger,
  teachingVerdict,
} from "../teachingRoom.js";
// DOOR-1 — her own door. The LAW decides which of the four states this screen is in and what a
// passcode change must carry; this file draws it and decides nothing (R28/R29).
import {
  PASSCODE_DOOR,
  PASSCODE_MIN,
  PASSCODE_ROTATE_LABEL,
  PASSCODE_ROTATE_LEDE,
  PASSCODE_SET_LABEL,
  PASSCODE_SET_LEDE,
  passcodeDoorState,
  passcodeSubmitBody,
  passcodeSubmitVerdict,
} from "../supportView.js";

export type Teaching = {
  id: number;
  classKey: string;
  subject: string;
  body: string;
  state: string;
  authorEmail: string;
  overrode: { title: string; excerpt: string; source: string }[];
  createdAt: string | null;
  updatedAt: string | null;
  retiredAt: string | null;
};

/** >>> HER CLAUSE 4's TEST DRAWER POSTS TO THE MEMBER DOOR ITSELF. <<< Not a private endpoint — see
 *  `server/teaching-routes.js`'s note at the absence: what this drawer has to produce is the answer a
 *  member would get, and a second door is a second retrieval, a second gate wiring and a second turn
 *  ledger that agree today. The conversation id keeps a test out of a thread she is really using. */
export const TEACHING_TEST_CONVERSATION = "teaching-room-test";
export const TEACHING_CHAT_ROUTE = "/api/lala/chat";

const asJson = async (res: Response) => {
  try { return await res.json(); } catch { return {}; }
};

function ConflictNote({ rows }: { rows: Teaching["overrode"] }) {
  // T9-2's no-body-no-box law: a teaching that overrode nothing draws no heading over nothing.
  if (!rows?.length) return null;
  return (
    <div className="teach-row__overrode">
      <span className="teach-row__overrode-head">You overrode</span>
      {rows.map((r, i) => (
        <span className="teach-row__overrode-item" key={`${r.title}-${i}`}>
          <b>{r.title}</b> {r.excerpt}
        </span>
      ))}
    </div>
  );
}

/** >>> HER CLAUSE 1's SECOND LOCK, ASKED HERE AND ENFORCED AT EVERY DOOR. <<< The component's own
 *  form is a CONVENIENCE — `requireSupport` refuses every route on the server whatever this
 *  screen draws, so a client that skipped the form reaches nothing (W1-1's shape: the box is the
 *  door's FACE, the server is the door). It is the SAME passcode the support view uses, per her
 *  clause 1's own parenthesis, so she is not carrying two keys. */
export function OwnerTeaching() {
  // DOOR-1: `passcodeSet` is THREE-VALUED. `null` is *we could not ask*, which is not *she has none*.
  const [gate, setGate] = useState<{ passcodeSet: boolean | null; unlocked: boolean; message?: string } | null>(null);
  const [entry, setEntry] = useState("");
  const [unlockNote, setUnlockNote] = useState("");
  const [pcCurrent, setPcCurrent] = useState("");
  const [pcNext, setPcNext] = useState("");
  const [pcConfirm, setPcConfirm] = useState("");
  const [pcNote, setPcNote] = useState("");
  const [rotating, setRotating] = useState(false);
  const [rows, setRows] = useState<Teaching[] | null>(null);
  const [note, setNote] = useState("");
  const [classKey, setClassKey] = useState(TEACHING_CLASSES[0].key);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [question, setQuestion] = useState("");
  const [reply, setReply] = useState("");
  const [asking, setAsking] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/teachings", { credentials: "include" });
      const data = await asJson(res);
      if (!res.ok || !data?.ok) { setRows([]); setNote(data?.message || "Your teachings could not be read just now."); return; }
      setRows(Array.isArray(data.teachings) ? data.teachings : []);
      setNote("");
    } catch {
      // R18/R64: a read that has not arrived and a read that FAILED are two facts, and only the
      // second one may say so. `rows` stays null until an answer lands, so the surface holds still.
      setRows([]);
      setNote("Your teachings could not be read just now.");
    }
  }, []);

  const readGate = useCallback(async () => {
    // >>> DOOR-1 — A FAILED READ IS NOT *SHE HAS NO PASSCODE* (R18), AND WITH A SETTER ON THIS
    // SCREEN THAT DISTINCTION BECAME LOAD-BEARING. <<< Both arms wrote `passcodeSet: false`, which
    // was harmless while nothing branched on it and is a **first-run setter shown to an owner who
    // has had a passcode for months** the moment something does — and the server would then refuse
    // her for want of the current one, which reads as the room being broken. `null` is *we could not
    // ask*, and `passcodeDoorState` holds still on it.
    try {
      const res = await fetch("/api/owner/support/status", { credentials: "include" });
      const data = await asJson(res);
      if (!res.ok || !data?.ok) { setGate({ passcodeSet: null, unlocked: false, message: "That could not be checked." }); return; }
      setGate({ passcodeSet: Boolean(data.passcodeSet), unlocked: Boolean(data.unlocked), message: String(data.message || "") });
    } catch {
      setGate({ passcodeSet: null, unlocked: false, message: "That could not be checked." });
    }
  }, []);

  /** >>> DOOR-1 — HER OWN DOOR, ONE SUBMITTER FOR BOTH ACTS. <<< A first run and a rotate are two
   *  facts (R18) and ONE route: `passcodeSubmitBody` decides what rides, so a first run cannot send
   *  an empty `current` the server would read as an assertion, and a rotate cannot forget it. Two
   *  submitters would be two answers to *what changing a passcode is* (R28/R29). */
  const submitPasscode = async (mode: "set" | "rotate") => {
    if (busy) return;
    const verdict = passcodeSubmitVerdict({ mode, current: pcCurrent, next: pcNext, confirm: pcConfirm });
    if (!verdict.ok) { setPcNote(verdict.message); return; }
    setBusy(true);
    try {
      const res = await fetch("/api/owner/support/passcode", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(passcodeSubmitBody({ mode, current: pcCurrent, next: pcNext })),
      });
      const data = await asJson(res);
      // R39/R47: this door refuses in the PAYLOAD as well, so the status code is never the answer.
      if (!res.ok || !data?.ok) { setPcNote(String(data?.message || "That could not be saved.")); return; }
      setPcCurrent(""); setPcNext(""); setPcConfirm("");
      // The server deliberately does NOT unlock the session that changed it — she proves the new one
      // once, like anyone else — so the gate is re-read rather than assumed.
      setPcNote(String(data.message || "Saved."));
      await readGate();
    } finally { setBusy(false); }
  };

  const unlock = async () => {
    if (busy || !entry) return;
    setBusy(true);
    try {
      const res = await fetch("/api/owner/support/unlock", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passcode: entry }),
      });
      const data = await asJson(res);
      if (!res.ok || !data?.ok) { setUnlockNote(String(data?.message || "That could not be checked.")); return; }
      setEntry("");
      setUnlockNote("");
      await readGate();
    } finally { setBusy(false); }
  };

  useEffect(() => { void readGate(); }, [readGate]);
  // The ledger is only ever read through the OPEN door: asking while locked puts a refusal note on
  // screen under a passcode form, which reads as the passcode having failed (OwnerVeil's own
  // recorded lesson one surface over).
  useEffect(() => { if (gate?.unlocked) void load(); }, [gate?.unlocked, load]);

  const ledger = useMemo(() => teachingLedger(rows || []), [rows]);
  const cls = teachingClass(classKey);
  // The verdict the DOOR will apply, asked here so she is told before she presses rather than after
  // (R28/R29: one law, and the form cannot offer what the door refuses — R15's shape).
  const verdict = useMemo(() => teachingVerdict({ classKey, subject, body }), [classKey, subject, body]);

  const reset = () => { setEditing(null); setSubject(""); setBody(""); setClassKey(TEACHING_CLASSES[0].key); };

  const save = async () => {
    if (busy || !verdict.ok) return;
    setBusy(true);
    try {
      const url = editing ? `/api/teachings/${editing}` : "/api/teachings";
      const res = await fetch(url, {
        method: editing ? "PATCH" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ classKey, subject, body }),
      });
      const data = await asJson(res);
      // R39/R47: these doors refuse in the PAYLOAD, so the status code is never the answer.
      if (!res.ok || !data?.ok) { setNote(data?.message || "That could not be saved."); return; }
      setNote(editing ? "Saved. She is teaching it now." : "Saved. She is teaching it now.");
      reset();
      await load();
    } finally { setBusy(false); }
  };

  const setState = async (id: number, state: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/teachings/${id}/state`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ state }),
      });
      const data = await asJson(res);
      if (!res.ok || !data?.ok) { setNote(data?.message || "That could not be changed."); return; }
      setNote(state === "retired" ? "Retired. She has stopped saying it." : "Back on. She is teaching it again.");
      await load();
    } finally { setBusy(false); }
  };

  const ask = async () => {
    if (asking || !question.trim()) return;
    setAsking(true);
    setReply("");
    try {
      const res = await fetch(TEACHING_CHAT_ROUTE, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: question.trim(), conversationId: TEACHING_TEST_CONVERSATION }),
      });
      const data = await asJson(res);
      setReply(String(data?.reply || data?.message || "She could not answer just now."));
    } catch {
      setReply("She could not answer just now.");
    } finally { setAsking(false); }
  };

  // THREE STATES, NEVER TWO (R18/L5.1): *we have not asked yet* HOLDS STILL, *she is locked out*
  // draws the form, and *she is through* draws the room. Collapsing the first two puts a passcode
  // prompt in front of an owner who is already unlocked, on every load.
  if (!gate) return null;
  const door = passcodeDoorState(gate);
  // DOOR-1: `wait` draws NOTHING, exactly as no answer does. A read that could not be made has its
  // own sentence and still offers no control, because offering the wrong one is worse than offering
  // none — a setter shown to an owner who has a passcode is refused by the server for want of the
  // current one, which reads as her own room being broken.
  if (door === PASSCODE_DOOR.WAIT) {
    return gate.message ? (
      <section className="teach teach--locked" data-owner-view="teaching" data-teaching-locked="true" data-passcode-door="wait">
        <h2 className="owner-h2">Teaching</h2>
        <p className="teach__note">{gate.message}</p>
      </section>
    ) : null;
  }

  // >>> HER FIRST RUN. SHE NEVER HUNTS A SETTINGS MENU FOR HER OWN ROOM. <<< The server has been
  // able to set this since D3-3 and nothing anywhere offered it; the sentence she met instead
  // pointed at "dashboard settings", which does not exist on this site.
  if (door === PASSCODE_DOOR.SET) {
    return (
      <section className="teach teach--locked" data-owner-view="teaching" data-teaching-locked="true" data-passcode-door="set">
        <h2 className="owner-h2">Teaching</h2>
        <p className="teach__lede">{gate.message || PASSCODE_SET_LEDE}</p>
        <div className="teach__unlock teach__unlock--set">
          <input className="teach__passcode" type="password" value={pcNext} placeholder={`New passcode (at least ${PASSCODE_MIN})`}
            data-passcode-next onChange={(e) => setPcNext(e.target.value)} />
          {/* Typed twice, because there is no way back: a mistyped passcode on a FIRST run locks her
              out of her own room and this site has no recovery path for it at all. */}
          <input className="teach__passcode" type="password" value={pcConfirm} placeholder="Type it again"
            data-passcode-confirm onChange={(e) => setPcConfirm(e.target.value)} />
          <button type="button" className="button teach__unlock-go" data-passcode-save
            disabled={busy || !passcodeSubmitVerdict({ mode: "set", next: pcNext, confirm: pcConfirm }).ok}
            onClick={() => void submitPasscode("set")}>{PASSCODE_SET_LABEL}</button>
        </div>
        {pcNote ? <p className="teach__note">{pcNote}</p> : null}
      </section>
    );
  }

  if (door === PASSCODE_DOOR.UNLOCK) {
    return (
      <section className="teach teach--locked" data-owner-view="teaching" data-teaching-locked="true" data-passcode-door="unlock">
        <h2 className="owner-h2">Teaching</h2>
        <p className="teach__lede">{gate.message || "Your passcode opens this room."}</p>
        <div className="teach__unlock">
          <input className="teach__passcode" type="password" value={entry} placeholder="Support passcode"
            data-teaching-passcode onChange={(e) => setEntry(e.target.value)} />
          <button type="button" className="button teach__unlock-go" disabled={busy || !entry} onClick={() => void unlock()}>Open it</button>
        </div>
        {unlockNote ? <p className="teach__note">{unlockNote}</p> : null}
      </section>
    );
  }

  return (
    <section className="teach" data-owner-view="teaching" data-passcode-door="open">
      <h2 className="owner-h2">Teaching</h2>
      <p className="teach__lede">
        What you write here reaches Lala the way your books do, and your word outranks them.
        It is live the moment you save it.
      </p>

      {/* >>> DOOR-1 — HER *CHANGEABLE ANYTIME* CLAUSE, FROM INSIDE THE ROOM. <<< It is behind a
          toggle rather than open on the page: this is a key rather than a setting, and a room whose
          first control is a passcode form is a room that reads as a lock instead of a desk. The
          CURRENT one is required, which is D3-3's server law and not this screen's opinion — a
          borrowed laptop with a live owner cookie must not be able to replace the second key. */}
      <div className="teach__rotate">
        <button type="button" className="button button--quiet teach__rotate-open" data-passcode-rotate-open
          aria-expanded={rotating} onClick={() => { setRotating((v) => !v); setPcNote(""); }}>
          {PASSCODE_ROTATE_LABEL}
        </button>
        {rotating ? (
          <div className="teach__unlock teach__unlock--rotate">
            <p className="teach__lede">{PASSCODE_ROTATE_LEDE}</p>
            <input className="teach__passcode" type="password" value={pcCurrent} placeholder="Your passcode now"
              data-passcode-current onChange={(e) => setPcCurrent(e.target.value)} />
            <input className="teach__passcode" type="password" value={pcNext} placeholder={`New passcode (at least ${PASSCODE_MIN})`}
              data-passcode-next onChange={(e) => setPcNext(e.target.value)} />
            <input className="teach__passcode" type="password" value={pcConfirm} placeholder="Type it again"
              data-passcode-confirm onChange={(e) => setPcConfirm(e.target.value)} />
            <button type="button" className="button teach__unlock-go" data-passcode-save
              disabled={busy || !passcodeSubmitVerdict({ mode: "rotate", current: pcCurrent, next: pcNext, confirm: pcConfirm }).ok}
              onClick={() => void submitPasscode("rotate")}>{PASSCODE_ROTATE_LABEL}</button>
          </div>
        ) : null}
        {pcNote ? <p className="teach__note">{pcNote}</p> : null}
      </div>

      <div className="teach__form">
        <div className="teach__classes" role="group" aria-label="What kind of teaching">
          {TEACHING_CLASSES.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`teach__class${classKey === c.key ? " is-on" : ""}`}
              data-class-key={c.key}
              aria-pressed={classKey === c.key}
              onClick={() => setClassKey(c.key)}
            >
              <b>{c.label}</b>
              <span>{c.hint}</span>
            </button>
          ))}
        </div>

        <label className="teach__field">
          <span>{cls?.subjectLabel || "This is about"}</span>
          <input
            className="teach__subject"
            value={subject}
            maxLength={TEACHING_SUBJECT_MAX}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>
        <label className="teach__field">
          <span>{cls?.bodyLabel || "In your own words"}</span>
          <textarea
            className="teach__body"
            value={body}
            maxLength={TEACHING_BODY_MAX}
            rows={5}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>

        {/* Her clause 5's refusal, NAMED. The law's own sentence, so the reason she is given is the
            reason the door would give — never a generic "that is not allowed". */}
        {!verdict.ok && subject.trim() && body.trim() ? (
          <p className="teach__refusal" data-reason={verdict.reason}>{verdict.message}</p>
        ) : null}

        <div className="teach__actions">
          <button type="button" className="button teach__save" disabled={busy || !verdict.ok} onClick={() => void save()}>
            {editing ? "Save the change" : "Teach her this"}
          </button>
          {editing ? (
            <button type="button" className="button button--quiet teach__cancel" onClick={reset}>Cancel</button>
          ) : null}
        </div>
        {note ? <p className="teach__note">{note}</p> : null}
      </div>

      {/* HER CLAUSE 4's TEST DRAWER, beside the form rather than on another screen: the point of it is
          that she sees how a teaching landed BEFORE members do. */}
      <div className="teach__test">
        <h3 className="teach__test-head">Ask her, before they do</h3>
        <input
          className="teach__question"
          value={question}
          placeholder="Ask her the thing you just taught her"
          onChange={(e) => setQuestion(e.target.value)}
        />
        <button type="button" className="button teach__ask" disabled={asking || !question.trim()} onClick={() => void ask()}>
          {asking ? "Asking her..." : "Ask her"}
        </button>
        {reply ? <p className="teach__reply">{reply}</p> : null}
      </div>

      {/* HER CLAUSE 4's LEDGER. Three states, never two (R18/R65): a read in flight HOLDS STILL, a
          read that arrived empty is an ANSWER, and a failure has its own sentence above. */}
      {rows === null ? null : ledger.length === 0 ? (
        <p className="teach__empty">You have not taught her anything yet.</p>
      ) : (
        <ul className="teach__ledger">
          {ledger.map((t) => (
            <li className={`teach-row${t.state === "retired" ? " is-retired" : ""}`} key={t.id} data-teaching-id={t.id} data-state={t.state}>
              <span className="teach-row__class">{teachingClass(t.classKey)?.label || t.classKey}</span>
              <b className="teach-row__subject">{t.subject}</b>
              <span className="teach-row__body">{t.body}</span>
              <ConflictNote rows={t.overrode} />
              <div className="teach-row__acts">
                <button
                  type="button" className="button button--quiet teach-row__edit"
                  onClick={() => { setEditing(t.id); setClassKey(t.classKey); setSubject(t.subject); setBody(t.body); }}
                >Edit</button>
                {/* Her ONE TAP, both ways. No confirm: what it destroys is nothing — the words stay
                    and the row comes back with the same tap (R35's `NO_ASK_BY_DESIGN` shape). */}
                <button
                  type="button" className="button button--quiet teach-row__retire"
                  onClick={() => void setState(t.id, t.state === "retired" ? "active" : "retired")}
                >{t.state === "retired" ? "Teach it again" : "Retire it"}</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
